import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  CaretDown,
  GraduationCap,
  ListBullets,
  Stack,
} from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Videos } from '../../../assets/svgs/Videos';
import { useLectureQbankStudyStart } from '../../../hooks/useLectureQbankStudyStart';
import { useLectureFlashcardStudyStart } from '../../../hooks/useLectureFlashcardStudyStart';
import { useChatDocumentViewerStatus } from '../../../providers/chatDocumentViewerStatus';
import { lectureDocumentPath } from '../../../providers/lectureDocumentSessionRequest';
import { postBulkLecturePreviews } from '../../../services/lectures';
import {
  JobStatus,
  mappingJobsSelectors,
} from '../../../slices/mappingJobsSlice/mappingJobsSlice';
import { StudyPhase, selectStudyPhase } from '../../../slices/study/studySlice';
import { useStudyFlow } from '../../../studyFlow';
import type { RootState } from '../../../store/store';
import type { LecturePreview } from '../../../types/Lecture';
import StartFlashcardModal from '../Study/StartFlashcardModal';
import StartQBankModal from '../Study/StartQBankModal';

/**
 * Starting study from a document is pointless (and confusing) once the
 * student is already in a study session: they opened the document mid-session
 * to read or check the source.
 */
const IN_SESSION_PHASES: StudyPhase[] = [
  StudyPhase.BLOCK_STUDY,
  StudyPhase.BLOCK_REVIEW,
  StudyPhase.FLASHCARD_STUDY,
  StudyPhase.VIDEO_STUDY,
];

/** While the lecture is still being mapped its counts grow; re-read them. */
const PENDING_MAPPING_REFRESH_MS = 5000;

const readPreview = async (lectureId: string) => {
  // The service, not the thunk: this one lecture must not replace the lectures
  // list (ORA-345).
  const { lectures: previews } = (await postBulkLecturePreviews({
    lectureIds: [lectureId],
  })) as { lectures: LecturePreview[] | undefined };

  return previews?.[0] ?? null;
};

type LectureStudyVariant = 'menu' | 'pills';

const LectureStudyActions = ({
  lectureId,
  preview,
  variant,
}: {
  lectureId: string;
  preview: LecturePreview;
  variant: LectureStudyVariant;
}) => {
  // Study started here comes back to this document, at the page on screen:
  // the deep link reopens the lecture's document session (ORA-736).
  const page = useChatDocumentViewerStatus()?.page ?? null;
  const returnPath = lectureDocumentPath(lectureId, page);
  const selectedLectures = useMemo(() => [preview], [preview]);

  const { openQbankModalForLectures, qbankModalProps } =
    useLectureQbankStudyStart(selectedLectures, {
      returnTo: 'documents',
      returnPath,
      selectionNounPlural: 'Documents',
    });

  const { openFlashcardModalForLectures, flashcardModalProps } =
    useLectureFlashcardStudyStart(selectedLectures, {
      returnTo: 'documents',
      returnPath,
      selectionNounPlural: 'Documents',
    });

  const { sendPhaseEvent } = useStudyFlow();

  const startVideos = useCallback(() => {
    sendPhaseEvent({
      type: 'START_MULTI_LECTURE_VIDEOS',
      lectureIds: [lectureId],
      returnTo: 'documents',
      returnPath,
    });
  }, [lectureId, returnPath, sendPhaseEvent]);

  const qbankCount = preview.vignettes?.total ?? 0;
  const flashcardCount = preview.flashcards?.total ?? 0;
  const videoCount = preview.videos?.total ?? 0;

  if (qbankCount === 0 && flashcardCount === 0 && videoCount === 0) {
    return null;
  }

  const videosIcon = (
    <span
      className="flex size-4 items-center [&_svg]:size-4"
      aria-hidden="true"
    >
      <Videos />
    </span>
  );

  // mcgraw-hill: QBank and Flashcards as pills above the chat input (no
  // videos in the McGraw Hill chapter viewer).
  if (variant === 'pills') {
    if (qbankCount === 0 && flashcardCount === 0) return null;

    return (
      <>
        <div className="mcgraw-hill-study-pills flex gap-2 pb-2">
          {qbankCount > 0 ? (
            <Button
              variant="glass"
              shape="round"
              className="flex-1"
              onClick={() => openQbankModalForLectures()}
            >
              <ListBullets aria-hidden="true" />
              Start QBank
            </Button>
          ) : null}
          {flashcardCount > 0 ? (
            <Button
              variant="glass"
              shape="round"
              className="flex-1"
              onClick={() => openFlashcardModalForLectures()}
            >
              <Stack aria-hidden="true" />
              Start Flashcards
            </Button>
          ) : null}
        </div>
        <StartQBankModal {...qbankModalProps} />
        <StartFlashcardModal {...flashcardModalProps} />
      </>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0"
            aria-label="Study this lecture"
          >
            <GraduationCap className="size-4" aria-hidden="true" />
            <span className="hidden mobile:inline">Study</span>
            <CaretDown className="size-3" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {qbankCount > 0 ? (
            <DropdownMenuItem onSelect={() => openQbankModalForLectures()}>
              <ListBullets aria-hidden="true" />
              Start QBank
            </DropdownMenuItem>
          ) : null}
          {flashcardCount > 0 ? (
            <DropdownMenuItem onSelect={() => openFlashcardModalForLectures()}>
              <Stack aria-hidden="true" />
              Start Flashcards
            </DropdownMenuItem>
          ) : null}
          {videoCount > 0 ? (
            <DropdownMenuItem onSelect={startVideos}>
              {videosIcon}
              Start Videos
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      <StartQBankModal {...qbankModalProps} />
      <StartFlashcardModal {...flashcardModalProps} />
    </>
  );
};

/**
 * The viewer toolbar's Study menu for a lecture (ORA-736): start its QBank,
 * flashcards or videos, as View Document's overlay did. Shown once the
 * lecture's mapped content is known and there is some, and never during a
 * study session.
 */
const LectureStudyMenu = ({
  lectureId,
  variant = 'menu',
}: {
  lectureId: string;
  /** The toolbar's dropdown, or (McGraw Hill) pills above the chat input. */
  variant?: LectureStudyVariant;
}) => {
  const studyPhase = useSelector(selectStudyPhase);
  const isInStudySession = IN_SESSION_PHASES.includes(studyPhase);
  const mappingJobs = useSelector(mappingJobsSelectors.selectAll);

  const hasPendingMappingJob = mappingJobs.some(
    (job) => job.lectureId === lectureId && job.status === JobStatus.PENDING
  );

  // A RESOURCE has nothing to study, and the preview endpoint refuses its id.
  const isResource = useSelector(
    (state: RootState) =>
      state.lectureTree?.nodesById[lectureId]?.nodeType === 'RESOURCE'
  );

  const [preview, setPreview] = useState<LecturePreview | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPreview(null);
    if (isResource) return;

    readPreview(lectureId)
      .then((next) => {
        if (!cancelled) setPreview(next);
      })
      .catch(() => {
        // No counts, no menu: the document itself is unaffected.
      });

    return () => {
      cancelled = true;
    };
  }, [isResource, lectureId]);

  useEffect(() => {
    if (!hasPendingMappingJob || isResource) return;
    let cancelled = false;

    const intervalId = window.setInterval(() => {
      readPreview(lectureId)
        .then((next) => {
          if (!cancelled && next) setPreview(next);
        })
        .catch(() => {
          // A transient failure keeps the last good counts.
        });
    }, PENDING_MAPPING_REFRESH_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [hasPendingMappingJob, isResource, lectureId]);

  if (isInStudySession || !preview) return null;

  return (
    <LectureStudyActions
      lectureId={lectureId}
      preview={preview}
      variant={variant}
    />
  );
};

export default LectureStudyMenu;
